// src/pages/MedicalCopilot.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import ChatInterface from "../components/ChatInterface";
import { auth } from "../services/supabase";
import { API_BASE_URL } from "../frontend_config";
import {
  Play,
  RotateCcw,
  Square,
  ClipboardList,
  FileText,
} from "lucide-react";

const MODULE = "module_3_medical_copilot";

export default function MedicalCopilot() {
  const [sessionId, setSessionId] = useState(null);
  const [sessionState, setSessionState] = useState("idle");
  const [requiredFields, setRequiredFields] = useState([]);

  const [complaint, setComplaint] = useState("");
  const [caseText, setCaseText] = useState("");
  const [activeComplaint, setActiveComplaint] = useState("");

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const complaintRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    complaintRef.current?.focus();
  }, []);

  const inPreStart = useMemo(() => {
    return !sessionId || sessionState === "idle" || sessionState === "needs_initialization";
  }, [sessionId, sessionState]);

  const canStart = useMemo(() => {
    return (complaint || "").trim().length > 0 && (caseText || "").trim().length > 0 && !isLoading;
  }, [complaint, caseText, isLoading]);

  const canChat = useMemo(() => {
    return !!sessionId && sessionState === "running" && !isLoading;
  }, [sessionId, sessionState, isLoading]);

  const appendMessage = (role, content, extra = {}) => {
    setMessages((prev) => [
      ...prev,
      { role, content, timestamp: new Date().toISOString(), ...extra },
    ]);
  };

  const setMessagesFromTranscript = (transcript) => {
    const safe = Array.isArray(transcript) ? transcript : [];
    const mapped = safe
      .filter((m) => (m?.role || "").toLowerCase() !== "system")
      .map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.ts || new Date().toISOString(),
      }));
    setMessages(mapped);
  };

  async function getAuthHeader() {
    const { data, error: sessErr } = await auth.getCurrentSession();
    if (sessErr) return {};
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async function apiFetch(url, options = {}) {
    const authHeader = await getAuthHeader();
    const headers = { ...(options.headers || {}), ...authHeader };
    return fetch(url, { ...options, headers });
  }

  async function createSession() {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ module: MODULE }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function initSession(id, payload) {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions/${id}/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function sendTurn(id, text) {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions/${id}/turn`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_message: text, payload: {} }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function endSessionApi(id) {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions/${id}/end`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload: { include_signals: true } }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  const onStartCase = async () => {
    if (!canStart) return;

    setIsLoading(true);
    setError(null);

    try {
      setMessages([]);

      const created = await createSession();
      const id = created.session_id;

      setSessionId(id);
      setSessionState(created.state || "needs_initialization");
      setRequiredFields(created.required_fields || []);

      const c = (complaint || "").trim();
      const ct = (caseText || "").trim();

      const initPayload = {
        complaint: c,
        case_text: ct,
      };

      const init = await initSession(id, initPayload);

      setSessionState(init.state || "running");
      setActiveComplaint(c);

      const transcript = init?.meta?.transcript;
      if (Array.isArray(transcript) && transcript.length > 0) {
        setMessagesFromTranscript(transcript);
      } else {
        appendMessage("user", ct);
        appendMessage("assistant", init.assistant_message || "Case loaded. What would you like help with?");
      }

      setTimeout(() => chatInputRef.current?.focus?.(), 0);
    } catch (e) {
      const msg = e?.message || String(e);
      setError(msg);
      appendMessage("assistant", `Failed to start case: ${msg}`, { isError: true });

      setSessionId(null);
      setSessionState("idle");
      setRequiredFields([]);
      setActiveComplaint("");
    } finally {
      setIsLoading(false);
    }
  };

  const onSend = async (text) => {
    const msg = (text || "").trim();
    if (!msg || !canChat) return;

    appendMessage("user", msg);
    setIsLoading(true);
    setError(null);

    try {
      const out = await sendTurn(sessionId, msg);
      setSessionState(out.state || "running");
      if (out.assistant_message) appendMessage("assistant", out.assistant_message);
    } catch (e) {
      const m = e?.message || String(e);
      setError(m);
      appendMessage("assistant", `Turn failed: ${m}`, { isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const onEnd = async () => {
    if (!sessionId || sessionState !== "running") return;

    setIsLoading(true);
    setError(null);

    try {
      const out = await endSessionApi(sessionId);
      setSessionState(out.state || "completed");
      appendMessage("assistant", out.assistant_message || "Session ended.");
    } catch (e) {
      const m = e?.message || String(e);
      setError(m);
      appendMessage("assistant", `End session failed: ${m}`, { isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const onResetUi = () => {
    setSessionId(null);
    setSessionState("idle");
    setRequiredFields([]);
    setMessages([]);
    setError(null);
    setIsLoading(false);
    setComplaint("");
    setCaseText("");
    setActiveComplaint("");
    setTimeout(() => complaintRef.current?.focus?.(), 0);
  };

  const onComplaintKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      document.getElementById("m3-case-details")?.focus?.();
    }
  };

  const onCaseTextKeyDown = (e) => {
    const isCtrlEnter = (e.ctrlKey || e.metaKey) && e.key === "Enter";
    if (isCtrlEnter) {
      e.preventDefault();
      onStartCase();
    }
  };

  return (
    <div className="module-page">
      <div className="module-header">
        <h1>Medical Copilot</h1>
        <p className="module-description">Session-based clinical reasoning co-pilot.</p>
      </div>

      {/* Status Bar + Setup Panel */}
      {inPreStart ? (
        <div className="patient-sim" style={{ padding: 0, minHeight: 'auto', maxWidth: '100%' }}>
          <div className="sim-status-bar">
            <span className="status-label">
              State: <strong>{sessionState}</strong>
            </span>
            {activeComplaint && (
              <span className="status-label">
                Complaint: <strong>{activeComplaint}</strong>
              </span>
            )}

            {Array.isArray(requiredFields) && requiredFields.length > 0 && (
              <span className="status-label">
                Required: {requiredFields.join(", ")}
              </span>
            )}

            <div className="status-actions">
              {sessionState === "running" && (
                <button className="btn btn-secondary" type="button" onClick={onEnd} disabled={isLoading}>
                  <Square size={14} />
                  End session
                </button>
              )}

              {!inPreStart && (
                <button className="btn btn-ghost" type="button" onClick={onResetUi} disabled={isLoading}>
                  <RotateCcw size={14} />
                  Reset
                </button>
              )}
            </div>
          </div>

          <div className="setup-panel">
            <div className="setup-title">
              <ClipboardList size={16} />
              Case details
            </div>

            <div className="field-group">
              <label htmlFor="m3-complaint">
                <FileText size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                Chief complaint (short)
              </label>
              <input
                ref={complaintRef}
                id="m3-complaint"
                value={complaint}
                onChange={(e) => setComplaint(e.target.value)}
                onKeyDown={onComplaintKeyDown}
                placeholder='e.g. "Chest pain"'
                disabled={isLoading}
                className="field-input"
              />
            </div>

            <div className="field-group">
              <label htmlFor="m3-case-details">Full clinical case details</label>
              <div className="field-hint">
                Include age/sex, symptoms, duration, PMH, meds, allergies, vitals, and investigations if available.
                (Tip: Ctrl/Cmd+Enter to start.)
              </div>
              <textarea
                id="m3-case-details"
                value={caseText}
                onChange={(e) => setCaseText(e.target.value)}
                onKeyDown={onCaseTextKeyDown}
                placeholder="Paste the full clinical case here..."
                rows={10}
                disabled={isLoading}
              />
            </div>

            <div className="setup-actions">
              <button className="btn btn-primary" type="button" onClick={onStartCase} disabled={!canStart}>
                <Play size={14} />
                {isLoading ? "Starting..." : "Start case"}
              </button>
            </div>

            {error && (
              <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--color-error, #dc2626)' }}>
                <strong>Error:</strong> {error}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="patient-sim" style={{ padding: 0, minHeight: 'auto', maxWidth: '100%' }}>
            <div className="sim-status-bar">
              <span className="status-label">
                State: <strong>{sessionState}</strong>
              </span>
              {activeComplaint && (
                <span className="status-label">
                  Complaint: <strong>{activeComplaint}</strong>
                </span>
              )}

              {Array.isArray(requiredFields) && requiredFields.length > 0 && (
                <span className="status-label">
                  Required: {requiredFields.join(", ")}
                </span>
              )}

              <div className="status-actions">
                {sessionState === "running" && (
                  <button className="btn btn-secondary" type="button" onClick={onEnd} disabled={isLoading}>
                    <Square size={14} />
                    End session
                  </button>
                )}

                <button className="btn btn-ghost" type="button" onClick={onResetUi} disabled={isLoading}>
                  <RotateCcw size={14} />
                  Reset
                </button>
              </div>
            </div>
          </div>
          <ChatInterface
            title="Clinical Decision Support"
            placeholder={
              sessionState !== "running"
                ? "Session ended. Reset UI to start a new case."
                : "Ask a question or provide an update..."
            }
            messages={messages}
            onSend={sessionState === "running" ? onSend : null}
            isLoading={isLoading}
            error={error}
            inputRef={chatInputRef}
            autoFocus={true}
          />
        </>
      )}
    </div>
  );
}
